import React from "react";
import { 
  Network, 
  FlaskConical, 
  ShieldCheck, 
  CheckCircle2
} from "lucide-react";

export default function OutcomeStoriesSection() {
  const stories = [
    {
      id: "electronics",
      title: "Global Electronics Manufacturer",
      tagline: "PROPOSAL SYNTHESIS AUTOMATION",
      description: "125+ component assemblies per proposal. A ~10-week historical engineering review process was reduced to hours. Validated against 50 historical proposals to ensure 100% technical accuracy before deployment.",
      icon: <Network className="w-5 h-5 text-brand-burgundy" />,
      metrics: ["10-Week Process → Hours", "125+ Components", "50-Proposal Validation"]
    },
    {
      id: "chemistry",
      title: "Industrial Chemistry R&D",
      tagline: "ORGANIZATIONAL KNOWLEDGE GOVERNANCE",
      description: "Unified disparate knowledge systems across complex internal laboratories, field trials, production facilities, and supplier intelligence. Governed access and semantic relationships across proprietary R&D data.",
      icon: <FlaskConical className="w-5 h-5 text-brand-burgundy" />,
      metrics: ["Lab-to-Production Scope", "Governed IP Access", "Semantic Entity Resolution"]
    },
    {
      id: "investment",
      title: "Regulated Investment Intelligence",
      tagline: "CONTROLLED DECISION SUPPORT",
      description: "Integrated multiple evidence sources and quantitative methods into a single, traceable decision-support platform. Enforced historical outcome validation to meet rigorous financial compliance standards.",
      icon: <ShieldCheck className="w-5 h-5 text-brand-burgundy" />,
      metrics: ["Quantitative Validation", "Multi-Source Traceability", "Compliance-Ready"]
    }
  ];

  return (
    <section id="outcomes" className="bg-brand-sand/40 py-24 md:py-28 border-y border-brand-sand relative">
      <div className="max-w-6xl mx-auto px-6">
        
        {/* Header Block with franchise board structure */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-xs font-mono uppercase tracking-widest text-brand-burgundy font-bold">02. Executive Outcomes</span>
              <div className="h-0.5 bg-brand-burgundy/40 w-12" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold font-display text-brand-charcoal tracking-tight">
              Evidence Over Assertions
            </h2>
            <p className="text-sm text-stone-600 mt-2 font-light">
              Executive judgment is built on recognizing expensive mistakes before they happen. These systems demonstrate what governed, scalable AI looks like in production.
            </p>
          </div>
          
          <div className="shrink-0 bg-brand-charcoal text-brand-sand text-[11px] font-mono py-2 px-4 rounded-sm border-l-4 border-brand-orange uppercase tracking-wider font-bold shadow-sm">
            Auditable Scale & Traceability
          </div>
        </div>

        {/* 3-Column Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {stories.map((story) => (
            <div 
              key={story.id}
              className="bg-white border border-brand-sand shadow-sm hover:shadow-md hover:border-brand-burgundy/30 transition-all p-8 flex flex-col group"
            >
              <div className="w-12 h-12 bg-brand-sand flex items-center justify-center rounded-sm mb-6 group-hover:scale-105 transition-transform">
                {story.icon}
              </div>
              
              <div className="text-[10px] font-mono text-brand-burgundy font-bold uppercase tracking-wider mb-2">
                {story.tagline}
              </div>
              
              <h3 className="text-xl font-bold font-display text-brand-charcoal leading-tight mb-4 group-hover:text-brand-burgundy transition-colors">
                {story.title}
              </h3>
              
              <p className="text-sm text-stone-600 font-light leading-relaxed mb-8 flex-grow">
                {story.description}
              </p>

              <div className="pt-6 border-t border-brand-sand/60 space-y-2">
                {story.metrics.map((metric, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-brand-orange mt-0.5 shrink-0" />
                    <span className="text-xs font-mono text-stone-600 uppercase tracking-widest">{metric}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
